import type { EditorCommand } from '@atlaskit/editor-common/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import {
	ActionType,
	type MauiDiagramPasteFlag,
	MauiEmbedFinalizeStatus,
	mauiDiagramPastePluginKey,
} from '../pm-plugins/maui-diagram-paste-plugin';

type MarkRemixWhiteboardNeedsSpaceSelectionArgs = {
	/** The remixed whiteboard's content id (parsed from the embed URL). */
	contentId: string;
	/**
	 * The bottom-left flag to show while the picker is open. Built by the caller
	 * (which has access to `intl`) using the shared `flagFinalizeSpacePicker*`
	 * messages so the copy matches the paste flow.
	 */
	flag?: MauiDiagramPasteFlag;
	/** The `~ROVO…` embed URL currently present in the document. */
	originalUrl: string;
};

/**
 * Registers a remixed whiteboard (whose automatic finalization failed) in the
 * maui-diagram paste plugin's finalize state as `NEEDS_SPACE_SELECTION`, so the
 * *shared* native-embed space picker can be opened for it.
 *
 * This is step 1 of the remix recovery flow; the caller follows it with
 * `openMauiEmbedSpacePickerCommand` (a separate transaction) to flip
 * `pickerOpen`. Splitting the two transitions is required because the plugin
 * reads a single meta per transaction.
 *
 * Reusing the paste plugin's state machine means the remix and paste flows
 * share one picker, one mount, and one finalize backend; on cancel the in-embed
 * banner re-shows, and on selection `finalizeAfterMove` runs.
 */
export const markRemixWhiteboardNeedsSpaceSelection =
	({ contentId, originalUrl, flag }: MarkRemixWhiteboardNeedsSpaceSelectionArgs): EditorCommand =>
	({ tr }) => {
		if (
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
			FeatureGates.getExperimentValue<boolean>(
				'maui-native-embed-finalize-diagram-anywhere',
				'value',
				false,
			)
		) {
			return null;
		}

		if (!contentId || !originalUrl) {
			return null;
		}

		tr.setMeta(mauiDiagramPastePluginKey, {
			type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
			contentId,
			originalUrl,
			status: MauiEmbedFinalizeStatus.NEEDS_SPACE_SELECTION,
			flag,
		});

		return tr;
	};
